/**
 * KOAI Socket.IO Hook
 * 실시간 통신 훅
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || window.location.origin;

export function useSocket() {
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);

  const connectSocket = useCallback(() => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.log('토큰이 없어 소켓 연결을 건너뜁니다.');
      return;
    }

    // 기존 연결이 있으면 해제
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
    
    socketRef.current = io(SOCKET_URL, {
      auth: { token },
      transports: ['polling', 'websocket'], // polling을 먼저 시도
      reconnection: true,
      reconnectionAttempts: 3,
      reconnectionDelay: 2000,
      timeout: 10000,
      forceNew: true
    });

    socketRef.current.on('connect', () => {
      setIsConnected(true);
      setError(null);
      console.log('소켓 연결됨');
      // 전역에서 확인 가능하도록
      window.socketConnected = true;
      window.socket = socketRef.current;
    });

    socketRef.current.on('disconnect', () => {
      setIsConnected(false);
      console.log('소켓 연결 해제됨');
      window.socketConnected = false;
    });

    socketRef.current.on('connect_error', (err) => {
      setError(err.message);
      setIsConnected(false);
      console.error('소켓 연결 오류:', err.message);
    });
  }, []);

  useEffect(() => {
    connectSocket();

    return () => {
      socketRef.current?.disconnect();
    };
  }, [connectSocket]);

  const emit = useCallback((event, data) => {
    socketRef.current?.emit(event, data);
  }, []);

  const on = useCallback((event, callback) => {
    socketRef.current?.on(event, callback);
    return () => socketRef.current?.off(event, callback);
  }, []);

  const off = useCallback((event, callback) => {
    socketRef.current?.off(event, callback);
  }, []);

  return {
    socket: socketRef.current,
    isConnected,
    error,
    emit,
    on,
    off,
    reconnect: connectSocket
  };
}

/**
 * Chat-specific socket hook
 */
export function useChatSocket() {
  const { socket, isConnected, emit, on, off } = useSocket();
  const [isStreaming, setIsStreaming] = useState(false);

  // Join a chat session room
  const joinSession = useCallback((sessionId) => {
    emit('chat:join', { sessionId });
  }, [emit]);

  // Leave a chat session room
  const leaveSession = useCallback((sessionId) => {
    emit('chat:leave', { sessionId });
  }, [emit]);

  // Send a message with streaming response
  const sendMessage = useCallback((message, sessionId, options = {}) => {
    console.log('📤 소켓으로 메시지 전송:', { message, sessionId, options, isConnected: socket?.connected });
    
    if (!socket || !socket.connected) {
      console.error('❌ 소켓이 연결되지 않음');
      return;
    }
    
    setIsStreaming(true);
    emit('chat:message', {
      message,
      sessionId,
      ...options
    });
  }, [emit, socket]);

  // Listen for streaming chunks
  const onChunk = useCallback((callback) => {
    return on('chat:chunk', callback);
  }, [on]);

  // Listen for message complete
  const onComplete = useCallback((callback) => {
    const wrappedCallback = (data) => {
      setIsStreaming(false);
      callback(data);
    };
    return on('chat:complete', wrappedCallback);
  }, [on]);

  // Listen for errors
  const onError = useCallback((callback) => {
    const wrappedCallback = (data) => {
      setIsStreaming(false);
      callback(data);
    };
    return on('chat:error', wrappedCallback);
  }, [on]);

  // Listen for chart data
  const onChart = useCallback((callback) => {
    return on('chat:chart', callback);
  }, [on]);

  // Listen for daily limit warnings/exceeded
  const onLimit = useCallback((callback) => {
    return on('chat:limit', callback);
  }, [on]);

  // Listen for processing status (검색/생성 중 안내)
  const onStatus = useCallback((callback) => {
    return on('chat:status', callback);
  }, [on]);

  return {
    socket,
    isConnected,
    isStreaming,
    joinSession,
    leaveSession,
    sendMessage,
    onChunk,
    onComplete,
    onError,
    onChart,
    onLimit,
    onStatus
  };
}

export default useSocket;
